/**
 * Admin Login Page Component
 *
 * Renders the sign-in form and, via an inline mode toggle, a "forgot password"
 * form that emails a reset link. The reset link itself lands on the separate
 * ResetPassword page.
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authApiService } from '../services/auth-api';
import { getDefaultAdminRoute } from '../constants/routes';
import '../styles/AdminLogin.css';

type Mode = 'login' | 'forgot';

const AdminLogin: React.FC = () => {
  const [mode, setMode] = useState<Mode>('login');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    setNotice('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(username, password);
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname;
      navigate(from || getDefaultAdminRoute(), { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setIsLoading(true);
    try {
      const message = await authApiService.forgotPassword(resetEmail.trim());
      setNotice(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <img
            src="/columbia_university_head.png"
            alt="Columbia University"
            className="login-logo"
          />
          <h1>CS Advising Chatbot</h1>
          <p className="login-subtitle">
            {mode === 'login'
              ? 'Sign in to access the advising chatbot'
              : 'Reset your admin password'}
          </p>
        </div>

        {mode === 'login' ? (
          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                required
                autoComplete="username"
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            <button type="submit" disabled={isLoading} className="login-button">
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
            <button
              type="button"
              className="login-link-button"
              onClick={() => switchMode('forgot')}
            >
              Forgot password?
            </button>
          </form>
        ) : (
          <form onSubmit={handleForgotSubmit} className="login-form">
            <div className="form-group">
              <label htmlFor="reset-email">Email</label>
              <input
                id="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your account email"
                required
                autoComplete="email"
                autoFocus
              />
            </div>
            {error && <div className="login-error">{error}</div>}
            {notice && <div className="login-notice">{notice}</div>}
            <button type="submit" disabled={isLoading} className="login-button">
              {isLoading ? 'Sending...' : 'Send reset link'}
            </button>
            <button
              type="button"
              className="login-link-button"
              onClick={() => switchMode('login')}
            >
              Back to sign in
            </button>
          </form>
        )}

        <div className="login-footer">
          <p>Don't have an admin account? Contact your administrator.</p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
